import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  Tabs,
  Tab,
  Paper,
  IconButton,
  CircularProgress,
} from '@mui/material';
import {
  Building2,
  Upload,
  FileSpreadsheet,
  Plus,
  TrendingUp,
  Clock,
  X,
} from 'lucide-react';
import Header from '../../layout/Header';
import AddBuildingWizard from './components/AddBuildingWizard';
import ActionInbox from './components/ActionInbox';
import BuildingsGrid from './components/BuildingsGrid';
import UnitsGrid from './components/UnitsGrid';
import ImportWizard from './components/ImportWizard';
import { useAuth } from '../../../AuthProvider';
import { useLocation } from 'react-router-dom';

const RentAdvisor = () => {
  const { getAccessToken } = useAuth();
  const location = useLocation();
  const [hasData, setHasData] = useState(false);
  const [checkingData, setCheckingData] = useState(true);
  const [showAddBuilding, setShowAddBuilding] = useState(false);
  const [showImportWizard, setShowImportWizard] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [analyzingBuildings, setAnalyzingBuildings] = useState([]);
  const [sideBarState, setSideBarState] = useState(false);
  const [buildingsRefreshTrigger, setBuildingsRefreshTrigger] = useState(0);
  const [initialBuildingFromDiscover, setInitialBuildingFromDiscover] =
    useState(null);
  const [initialBuildingFilter, setInitialBuildingFilter] = useState(null);

  const getInitialAnalyzingBuildings = () => {
    if (typeof window === 'undefined') return [];
    try {
      const stored = localStorage.getItem('analyzingBuildings');
      if (stored) {
        const buildings = JSON.parse(stored);
        return buildings.filter(b => b.estimatedCompletion > Date.now());
      }
    } catch (error) {
      console.error('Error loading analyzing buildings:', error);
    }
    return [];
  };

  useEffect(() => {
    const state = location.state;
    if (state && state.fromDiscover && state.address) {
      console.log(
        '[RentAdvisor] Received Add to Portfolio request from Discover',
        state,
      );
      setInitialBuildingFromDiscover({
        buildingAddress: state.address,
      });
      setShowAddBuilding(true);
    }
  }, [location.state]);

  useEffect(() => {
    setAnalyzingBuildings(getInitialAnalyzingBuildings());
  }, []);

  useEffect(() => {
    const checkForBuildings = async () => {
      try {
        setCheckingData(true);
        const accessToken = getAccessToken();
        const url = new URL(
          `${process.env.REACT_APP_NODE_URL}/buildings/buildings`,
        );
        url.searchParams.append('page', '1');
        url.searchParams.append('limit', '1');

        const response = await fetch(url.toString(), {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.ok) {
          const data = await response.json();
          if (data.status && data.data && data.data.length > 0) {
            setHasData(true);
          } else {
            setHasData(false);
          }
        } else {
          setHasData(false);
        }
      } catch (err) {
        console.error('Error checking for buildings:', err);
        setHasData(false);
      } finally {
        setCheckingData(false);
      }
    };

    checkForBuildings();
  }, [getAccessToken, buildingsRefreshTrigger]);

  useEffect(() => {
    if (analyzingBuildings.length > 0) {
      localStorage.setItem(
        'analyzingBuildings',
        JSON.stringify(analyzingBuildings),
      );
    } else {
      localStorage.removeItem('analyzingBuildings');
    }
  }, [analyzingBuildings]);

  useEffect(() => {
    if (analyzingBuildings.length === 0) return;

    const interval = setInterval(() => {
      setAnalyzingBuildings(prev => {
        const updated = prev.filter(b => b.estimatedCompletion > Date.now());
        if (updated.length !== prev.length && updated.length === 0) {
          setHasData(true);
        }
        return updated;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [analyzingBuildings.length]);

  const formatTimeRemaining = completionTime => {
    const now = Date.now();
    const remaining = Math.max(0, completionTime - now);
    const hours = Math.floor(remaining / (1000 * 60 * 60));
    const minutes = Math.floor((remaining % (1000 * 60 * 60)) / (1000 * 60));
    const seconds = Math.floor((remaining % (1000 * 60)) / 1000);
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const calculateProgress = (startTime, completionTime) => {
    const now = Date.now();
    const total = completionTime - startTime;
    const elapsed = now - startTime;
    return Math.min(100, Math.max(0, (elapsed / total) * 100));
  };

  const handleCancelBuilding = buildingId => {
    setAnalyzingBuildings(prev => prev.filter(b => b.id !== buildingId));
  };

  const handleBuildingClick = buildingName => {
    setInitialBuildingFilter(buildingName);
    setActiveTab(2); // Switch to Units tab
  };

  const handleAddBuilding = () => {
    const newBuilding = {
      id: Date.now().toString(),
      address: '123 Main St',
      unitCount: 5,
      startTime: Date.now(),
      estimatedCompletion: Date.now() + 48 * 60 * 60 * 1000,
    };
    setAnalyzingBuildings(prev => [...prev, newBuilding]);
    setShowAddBuilding(false);
    setHasData(true);
  };

  if (showImportWizard) {
    return (
      <Box sx={{ minHeight: '100vh', backgroundColor: '#F9F9F9' }}>
        <Box sx={{ paddingX: { md: '1.25rem', xs: '0' } }}>
          <Header sideBarState={setSideBarState} pageTitle='Rent Advisor' />
        </Box>
        <ImportWizard
          onComplete={() => {
            setShowImportWizard(false);
            setHasData(true);
          }}
          onCancel={() => setShowImportWizard(false)}
        />
      </Box>
    );
  }

  if (checkingData) {
    return (
      <Box sx={{ backgroundColor: '#F9F9F9' }}>
        <Box sx={{ paddingX: { md: '1.25rem', xs: '0' } }}>
          <Header sideBarState={setSideBarState} pageTitle='Rent Advisor' />
        </Box>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 'calc(100vh - 8rem)', md: 'calc(100vh - 10rem)' },
          }}
        >
          <CircularProgress sx={{ color: '#39585C' }} />
        </Box>
      </Box>
    );
  }

  if (!hasData && !showImportWizard && !showAddBuilding) {
    return (
      <Box sx={{ backgroundColor: '#F9F9F9' }}>
        <Box sx={{ paddingX: { md: '1.25rem', xs: '0' } }}>
          <Header sideBarState={setSideBarState} pageTitle='Rent Advisor' />
        </Box>

        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 'calc(100vh - 8rem)', md: 'calc(100vh - 10rem)' },
            padding: { xs: '1rem', sm: '1.5rem', md: '2rem' },
          }}
        >
          <Card
            sx={{
              maxWidth: '800px',
              width: '100%',
              overflow: 'hidden',
              borderRadius: { xs: '0.5rem', md: '0.75rem' },
            }}
          >
            {analyzingBuildings.length > 0 && (
              <Box
                sx={{
                  backgroundColor: '#E8F0F1',
                  borderBottom: '1px solid #E5E7EB',
                  padding: '1rem',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: { xs: 'start', sm: 'center' },
                    justifyContent: 'space-between',
                    marginBottom: '1rem',
                    flexDirection: { xs: 'column', sm: 'row' },
                    gap: { xs: '0.75rem', sm: 0 },
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.75rem',
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    <Box
                      sx={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '0.5rem',
                        backgroundColor: '#4FA490',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <TrendingUp size={16} color='white' />
                    </Box>
                    <Box>
                      <Typography
                        sx={{
                          fontSize: '0.875rem',
                          fontWeight: 600,
                          color: '#111827',
                        }}
                      >
                        Market Data Collection
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          color: '#6B7280',
                        }}
                      >
                        Real-time analysis status
                      </Typography>
                    </Box>
                  </Box>
                  {analyzingBuildings.length > 0 && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.25rem 0.625rem',
                        borderRadius: '9999px',
                        backgroundColor: '#E8F0F1',
                        border: '1px solid #4FA490',
                      }}
                    >
                      <Box
                        sx={{
                          width: '6px',
                          height: '6px',
                          borderRadius: '50%',
                          backgroundColor: '#4FA490',
                          animation:
                            'pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
                        }}
                      />
                      <Typography
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#4FA490',
                        }}
                      >
                        {analyzingBuildings.length} Active
                      </Typography>
                    </Box>
                  )}
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '0.5rem',
                    maxHeight: '130px',
                    overflowY: 'auto',
                  }}
                >
                  {analyzingBuildings.map(building => {
                    const progress = calculateProgress(
                      building.startTime,
                      building.estimatedCompletion,
                    );
                    const timeRemaining = formatTimeRemaining(
                      building.estimatedCompletion,
                    );

                    return (
                      <Box
                        key={building.id}
                        sx={{
                          padding: '0.75rem',
                          borderRadius: '0.5rem',
                          backgroundColor: '#FFFFFF',
                          border: '1px solid #E5E7EB',
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'start',
                            justifyContent: 'space-between',
                            gap: '0.75rem',
                            marginBottom: '0.5rem',
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              sx={{
                                fontSize: '0.875rem',
                                fontWeight: 600,
                                color: '#111827',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {building.address}
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: '#6B7280',
                              }}
                            >
                              {building.unitCount} units • Analyzing comps
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem',
                              flexShrink: 0,
                            }}
                          >
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.375rem',
                                padding: '0.125rem 0.5rem',
                                borderRadius: '0.25rem',
                                backgroundColor: '#E8F0F1',
                              }}
                            >
                              <Clock size={12} color='#4FA490' />
                              <Typography
                                sx={{
                                  fontSize: '0.75rem',
                                  fontFamily: 'monospace',
                                  fontWeight: 600,
                                  color: '#4FA490',
                                }}
                              >
                                {timeRemaining}
                              </Typography>
                            </Box>
                            <IconButton
                              size='small'
                              onClick={() => handleCancelBuilding(building.id)}
                              sx={{
                                width: '24px',
                                height: '24px',
                                '&:hover': { backgroundColor: '#FEE2E2' },
                              }}
                            >
                              <X size={16} color='#6B7280' />
                            </IconButton>
                          </Box>
                        </Box>
                        <Box
                          sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '0.25rem',
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                color: '#6B7280',
                              }}
                            >
                              Progress
                            </Typography>
                            <Typography
                              sx={{
                                fontSize: '0.75rem',
                                fontWeight: 600,
                                color: '#111827',
                              }}
                            >
                              {Math.round(progress)}%
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              width: '100%',
                              backgroundColor: '#E5E7EB',
                              borderRadius: '9999px',
                              height: '6px',
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                height: '100%',
                                backgroundColor: '#4FA490',
                                borderRadius: '9999px',
                                width: `${progress}%`,
                                transition: 'width 1s ease',
                              }}
                            />
                          </Box>
                        </Box>
                      </Box>
                    );
                  })}
                </Box>

                {analyzingBuildings.length === 0 && (
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      textAlign: 'center',
                      marginTop: '0.75rem',
                    }}
                  >
                    <Button
                      variant='text'
                      onClick={() => setShowAddBuilding(true)}
                      sx={{
                        color: '#4FA490',
                        textTransform: 'none',
                        fontWeight: 600,
                        padding: 0,
                        minWidth: 'auto',
                        '&:hover': { textDecoration: 'underline' },
                      }}
                    >
                      Add your first building
                    </Button>{' '}
                    to start collecting market data and pricing comps
                  </Typography>
                )}
              </Box>
            )}

            <Box
              sx={{
                padding: { xs: '1.5rem', sm: '2rem', md: '2.5rem' },
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                gap: { xs: '1rem', md: '1.5rem' },
              }}
            >
              <Box
                sx={{
                  width: { xs: '48px', md: '64px' },
                  height: { xs: '48px', md: '64px' },
                  borderRadius: '50%',
                  backgroundColor: '#E8F0F1',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Building2 size={24} color='#39585C' />
              </Box>

              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}
              >
                <Typography
                  sx={{
                    fontSize: { xs: '1.25rem', md: '1.5rem' },
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  Add Your Portfolio
                </Typography>
                <Typography
                  sx={{
                    fontSize: { xs: '0.75rem', md: '0.875rem' },
                    color: '#6B7280',
                    maxWidth: '28rem',
                    paddingX: { xs: '0.5rem', md: 0 },
                  }}
                >
                  Add your buildings and units to get pricing recommendations
                  and track performance vs market
                </Typography>
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: '0.75rem',
                  width: '100%',
                  maxWidth: '24rem',
                  paddingTop: '0.5rem',
                }}
              >
                <Button
                  variant='contained'
                  size='large'
                  fullWidth
                  onClick={() => setShowAddBuilding(true)}
                  startIcon={<Plus size={20} />}
                  sx={{
                    backgroundColor: '#39585C',
                    textTransform: 'none',
                    '&:hover': { backgroundColor: '#2A3F42' },
                  }}
                >
                  Add Building
                </Button>

                <Button
                  variant='outlined'
                  size='large'
                  fullWidth
                  onClick={() => {
                    setShowImportWizard(true);
                  }}
                  startIcon={<Upload size={20} />}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#D1D5DB',
                    color: '#374151',
                    '&:hover': {
                      borderColor: '#9CA3AF',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  Import from CSV/Excel
                </Button>

                <Button
                  variant='outlined'
                  size='large'
                  fullWidth
                  onClick={() => setHasData(true)}
                  startIcon={<FileSpreadsheet size={20} />}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#D1D5DB',
                    color: '#374151',
                    '&:hover': {
                      borderColor: '#9CA3AF',
                      backgroundColor: '#F9FAFB',
                    },
                  }}
                >
                  Try Sample Data
                </Button>
              </Box>

              <Box sx={{ paddingTop: '0.5rem' }}>
                <Button
                  variant='text'
                  onClick={async () => {
                    try {
                      const accessToken = getAccessToken();
                      const response = await fetch(
                        `${process.env.REACT_APP_NODE_URL}/buildings/buildings/template`,
                        {
                          method: 'GET',
                          headers: {
                            Authorization: `Bearer ${accessToken}`,
                          },
                        },
                      );

                      if (response.ok) {
                        const blob = await response.blob();
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = 'buildings-import-template.csv';
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        document.body.removeChild(a);
                      }
                    } catch (error) {
                      console.error('Error downloading template:', error);
                    }
                  }}
                  sx={{
                    fontSize: '0.875rem',
                    color: '#6B7280',
                    textTransform: 'none',
                    '&:hover': { color: '#39585C' },
                  }}
                >
                  Download CSV Template
                </Button>
              </Box>
            </Box>
          </Card>
        </Box>
      </Box>
    );
  }

  if (showAddBuilding) {
    return (
      <Box sx={{ backgroundColor: '#F9F9F9' }}>
        <Box sx={{ paddingX: { md: '1.25rem', xs: '0' } }}>
          <Header sideBarState={setSideBarState} pageTitle='Rent Advisor' />
        </Box>
        <AddBuildingWizard
          onComplete={() => {
            setShowAddBuilding(false);
            setHasData(true);
            setBuildingsRefreshTrigger(prev => prev + 1);
            setInitialBuildingFromDiscover(null);
          }}
          onCancel={() => {
            setShowAddBuilding(false);
            setInitialBuildingFromDiscover(null);
          }}
          initialBuilding={initialBuildingFromDiscover}
        />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#F9F9F9' }}>
      <Box sx={{ paddingX: { md: '1.25rem', xs: '0' } }}>
        <Header sideBarState={setSideBarState} pageTitle='Rent Advisor' />
      </Box>

      <Box
        sx={{
          paddingX: { md: '1.25rem', xs: '1rem' },
          paddingY: '1.5rem',
        }}
      >
        <Paper
          sx={{
            backgroundColor: '#FFFFFF',
            borderRadius: { xs: '0.5rem', md: '0.75rem' },
            overflow: 'hidden',
            display: 'flex',
            flexDirection: 'column',
            height: { xs: 'calc(100vh - 120px)', md: 'calc(100vh - 150px)' },
          }}
        >
          <Box sx={{ borderBottom: '1px solid #E5E7EB', overflowX: 'auto' }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              variant='scrollable'
              scrollButtons='auto'
              sx={{
                '& .MuiTab-root': {
                  textTransform: 'none',
                  fontWeight: 500,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: '#7B8A8B',
                  minWidth: { xs: '80px', sm: '120px' },
                  paddingX: { xs: '0.75rem', sm: '1rem' },
                  '&.Mui-selected': {
                    color: '#39585C',
                  },
                },
                '& .MuiTabs-indicator': {
                  backgroundColor: '#39585C',
                },
              }}
            >
              <Tab label='Action Inbox' />
              <Tab label='Buildings' />
              <Tab label='Units' />
            </Tabs>
          </Box>

          <Box
            sx={{
              padding: 0,
              height: { xs: 'calc(100vh - 170px)', md: 'calc(100vh - 200px)' },
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            {activeTab === 0 && <ActionInbox />}
            {activeTab === 1 && (
              <BuildingsGrid
                onAddUnits={building => {
                  setShowAddBuilding(true);
                }}
                refreshTrigger={buildingsRefreshTrigger}
                onAddBuilding={() => setShowAddBuilding(true)}
                onImport={() => setShowImportWizard(true)}
                onDownloadTemplate={() => {
                  console.log('Download CSV Template');
                }}
                onBuildingClick={handleBuildingClick}
              />
            )}
            {activeTab === 2 && (
              <UnitsGrid
                initialBuildingFilter={initialBuildingFilter}
                onFilterApplied={() => setInitialBuildingFilter(null)}
              />
            )}
          </Box>
        </Paper>
      </Box>
    </Box>
  );
};

export default RentAdvisor;
